import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import { Camera, X, ImageOff, Loader2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { fileUrl, thumbUrl } from "../../lib/fileUrl";
import { visibleFatturaHistory } from "./fatturaHistory";

const resolveImg = (path) => fileUrl(path);

/**
 * Sheet a schermo intero per selezionare una foto fattura da associare al lotto.
 * Mostra:
 *  - Grande pulsante "Nuovo Scatto" (apre camera / file picker)
 *  - Grid con le ultime 10 fatture caricate dall'utente, ognuna cliccabile
 */
const FatturaSheet = ({
  open, onOpenChange, storico = [], localPreviews = {}, onSelectStorico, onNuovoScatto, uploading,
}) => {
  const [previewPath, setPreviewPath] = useState(null);
  const visibleStorico = visibleFatturaHistory(storico, localPreviews);
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl p-0" data-testid="fattura-sheet">
          <div className="p-5 pb-3 border-b border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Camera className="h-5 w-5 text-indigo-500" />
                Carica Fattura
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-1">
                Scatta una nuova foto o riseleziona una delle ultime 10 fatture caricate.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-5 space-y-4">
            {/* Nuovo Scatto */}
            <button
              type="button"
              onClick={onNuovoScatto}
              disabled={uploading}
              data-testid="fattura-nuovo-scatto-btn"
              className={`w-full flex items-center justify-center gap-3 h-16 rounded-2xl font-bold text-base transition-all ${
                uploading
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-md active:scale-[0.98]"
              }`}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Caricamento…</span>
                </>
              ) : (
                <>
                  <Camera className="h-5 w-5" />
                  <span>Nuovo Scatto</span>
                </>
              )}
            </button>

            {/* Storico */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase font-bold text-muted-foreground tracking-wide">
                  Storico ultime 10 fatture
                </p>
                <span className="text-[10px] text-muted-foreground" data-testid="fattura-storico-count">
                  {visibleStorico.length}/10
                </span>
              </div>
              {visibleStorico.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center border-2 border-dashed border-border rounded-2xl">
                  <ImageOff className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Ancora nessuna fattura caricata</p>
                  <p className="text-xs text-muted-foreground/80 mt-0.5">
                    Le fatture caricate qui appariranno per il riutilizzo veloce.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="fattura-storico-grid">
                  {visibleStorico.map((item, i) => {
                    const src = localPreviews[item.path] || resolveImg(item.path);
                    return (
                      <div
                        role="button"
                        tabIndex={0}
                        key={`${item.path}-${i}`}
                        onClick={() => onSelectStorico?.(item.path)}
                        className="group relative aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 border border-border shadow-sm active:scale-[0.97] transition-all cursor-pointer"
                        data-testid={`fattura-storico-item-${i}`}
                      >
                        <img
                          src={localPreviews[item.path] || thumbUrl(item.path)}
                          alt={`Fattura ${i + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            if (!e.currentTarget.dataset.full) { e.currentTarget.dataset.full = "1"; e.currentTarget.src = src; }
                            else { e.currentTarget.style.display = "none"; }
                          }}
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-left">
                          <p className="text-white text-[10px] font-semibold">
                            {item.last_used_at ? new Date(item.last_used_at).toLocaleDateString("it-IT") : "—"}
                          </p>
                        </div>
                        <button type="button" aria-label="Apri anteprima fattura"
                          className="absolute top-1.5 right-1.5 bg-white/95 rounded-full p-1.5 shadow"
                          onClick={(e) => { e.stopPropagation(); toast.dismiss(); setPreviewPath(src); }}
                          data-testid={`fattura-storico-preview-${i}`}
                        >
                          <Eye className="h-4 w-4 text-indigo-600" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview full-screen come Dialog Radix ANNIDATO: chiude solo l'anteprima */}
      <DialogPrimitive.Root open={!!previewPath} onOpenChange={(o) => { if (!o) setPreviewPath(null); }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content
            className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4 focus:outline-none"
            onClick={() => setPreviewPath(null)}
            data-testid="fattura-preview-fullscreen"
          >
            <DialogPrimitive.Title className="sr-only">Anteprima fattura</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Foto fattura selezionata.</DialogPrimitive.Description>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                data-testid="fattura-preview-close"
                aria-label="Chiudi anteprima"
              >
                <X className="h-6 w-6" />
              </button>
            </DialogPrimitive.Close>
            {previewPath && (
              <img
                src={previewPath}
                alt="Anteprima fattura"
                data-testid="fattura-sheet-preview-image"
                className="max-w-full max-h-full object-contain rounded-xl"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
};

export default FatturaSheet;
